import { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import clsx from 'clsx';

interface ViewLike {
  _id: string;
  name: string;
}

interface SaveViewModalProps<F> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, filters: F) => Promise<void>;
  onUpdate?: (id: string, filters: F) => Promise<void>;
  currentFilters: F;
  existingViews?: ViewLike[];
}

export function SaveViewModal<F>({ isOpen, onClose, onSubmit, onUpdate, currentFilters, existingViews = [] }: SaveViewModalProps<F>) {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setError('');
      setSelectedViewId(null);
    }
  }, [isOpen]);

  const selectedView = selectedViewId ? existingViews.find(v => v._id === selectedViewId) : null;
  const isUpdateMode = !!selectedView;

  const handleSubmit = async () => {
    setError('');
    if (isUpdateMode && selectedView && onUpdate) {
      try {
        setIsSubmitting(true);
        await onUpdate(selectedView._id, currentFilters);
        onClose();
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to update view');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!name.trim()) {
        setError('View name is required');
        return;
      }
      try {
        setIsSubmitting(true);
        await onSubmit(name.trim(), currentFilters);
        onClose();
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to save view');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSelectView = (view: ViewLike) => {
    setSelectedViewId(view._id);
    setError('');
  };

  const handleDeselectView = () => {
    setSelectedViewId(null);
    setError('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isUpdateMode ? 'Update View' : 'Save Current View'}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" isLoading={isSubmitting} onClick={handleSubmit}>
            {isUpdateMode ? 'Update' : 'Save View'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
        <div className="relative">
          <Input
            ref={inputRef}
            label="View Name"
            type="text"
            placeholder={isUpdateMode ? selectedView!.name : 'e.g., Jazz clubs in Paris'}
            value={isUpdateMode ? '' : name}
            onChange={(e) => setName(e.target.value)}
            autoFocus={!isUpdateMode}
            readOnly={isUpdateMode}
            className={isUpdateMode ? 'opacity-50' : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
          />
          {isUpdateMode && (
            <div
              onClick={handleDeselectView}
              className="absolute inset-0 cursor-pointer"
            />
          )}
        </div>
        {existingViews.length > 0 && (
          <div>
            <p className="text-xs text-dark-text-muted mb-2">Or update an existing view:</p>
            <div className="flex flex-wrap gap-1.5">
              {existingViews.map((view) => (
                <button
                  key={view._id}
                  onClick={() => handleSelectView(view)}
                  className={clsx(
                    'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors',
                    selectedViewId === view._id
                      ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                      : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                  )}
                >
                  {view.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-dark-text-muted">
          {isUpdateMode
            ? `This will update "${selectedView!.name}" with your current filter settings.`
            : 'This will save your current filter settings so you can quickly apply them later.'}
        </p>
      </div>
    </Modal>
  );
}
